import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Check, Zap, Globe, Shield, FileText, Settings, Layers, ArrowRight, Menu, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sticky Header Component
const StickyHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      data-testid="sticky-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2" data-testid="logo">
          <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <span className="font-['Outfit'] text-xl font-bold text-white">Hyper Local</span>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          <a href="#benefits" className="text-slate-400 hover:text-white transition-colors" data-testid="nav-benefits">Benefits</a>
          <a href="#how-it-works" className="text-slate-400 hover:text-white transition-colors" data-testid="nav-how-it-works">How it Works</a>
          <a href="#pricing" className="text-slate-400 hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
          <a href="#faq" className="text-slate-400 hover:text-white transition-colors" data-testid="nav-faq">FAQ</a>
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <button 
            onClick={() => navigate('/login')}
            className="text-slate-400 hover:text-white transition-colors"
            data-testid="login-btn"
          >
            Log in
          </button>
          <a 
            href="#pricing" 
            className="btn-primary text-sm py-3 px-6"
            data-testid="header-cta"
          >
            Create My First Service-Area Page
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden glass border-t border-white/10">
          <nav className="flex flex-col p-6 gap-4">
            <a href="#benefits" className="text-slate-300 py-2" onClick={() => setMobileMenuOpen(false)}>Benefits</a>
            <a href="#how-it-works" className="text-slate-300 py-2" onClick={() => setMobileMenuOpen(false)}>How it Works</a>
            <a href="#pricing" className="text-slate-300 py-2" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href="#faq" className="text-slate-300 py-2" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
            <button onClick={() => navigate('/login')} className="text-slate-300 py-2 text-left">Log in</button>
            <a href="#pricing" className="btn-primary text-center mt-2" onClick={() => setMobileMenuOpen(false)}>Create My First Page</a>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden grid-bg" data-testid="hero-section">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/20 via-transparent to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
        {/* Left side - Content */}
        <div className="space-y-8">
          <div className="overline">SEO Automation Built for Local Service Businesses</div>
          
          <h1 className="font-['Outfit'] text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight gradient-text" data-testid="hero-headline">
            Rank in Every City You Serve — Without Writing Hundreds of Pages
          </h1>
          
          <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-xl">
            Hyper Local automatically creates SEO-ready "Service + City" pages inside WordPress, so local businesses can win more searches without agencies or manual work.
          </p>

          {/* Credibility bullets - Benefits focused with full value statements */}
          <div className="space-y-3">
            <div className="flex items-start gap-3 text-slate-300">
              <Check className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <span>Create publish-ready WordPress drafts in minutes — nothing goes live without your approval</span>
            </div>
            <div className="flex items-start gap-3 text-slate-300">
              <Check className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <span>Google-friendly URLs that match how top-ranking competitors structure their pages</span>
            </div>
            <div className="flex items-start gap-3 text-slate-300">
              <Check className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <span>Automatic internal links so your city pages rank faster together — not as isolated SEO islands</span>
            </div>
            <div className="flex items-start gap-3 text-slate-300">
              <Check className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <span>Works seamlessly with Yoast and RankMath — no reconfiguration, no SEO guesswork</span>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <a 
              href="#pricing" 
              className="btn-primary text-center flex items-center justify-center gap-2"
              data-testid="hero-cta-primary"
            >
              Create My First Service-Area Page
              <ArrowRight className="w-5 h-5" />
            </a>
            <a 
              href="#how-it-works" 
              className="btn-secondary text-center"
              data-testid="hero-cta-secondary"
            >
              See How It Works
            </a>
          </div>

          <p className="text-sm text-slate-500">No credit card required • Draft-first • Cancel anytime</p>
        </div>

        {/* Right side - Preview Card Mock */}
        <div className="relative">
          <div className="card-base p-6 space-y-4 neon-glow" data-testid="preview-card">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <span className="text-slate-400 text-sm">WordPress Draft Preview</span>
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/50" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                <div className="w-3 h-3 rounded-full bg-green-500/50" />
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wide">Title</label>
                <div className="text-lg font-semibold text-white mt-1">Roof Repair in Austin, TX</div>
              </div>
              
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wide">Slug</label>
                <div className="text-cyan-400 font-mono text-sm mt-1">/roof-repair-austin/</div>
              </div>
              
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wide">Content Blocks</label>
                <div className="mt-2 space-y-2">
                  <div className="bg-slate-800/50 rounded p-2 text-sm text-slate-300">Hero Section</div>
                  <div className="bg-slate-800/50 rounded p-2 text-sm text-slate-300">Service Description</div>
                  <div className="bg-slate-800/50 rounded p-2 text-sm text-slate-300">Local Info + Map</div>
                  <div className="bg-slate-800/50 rounded p-2 text-sm text-slate-300">CTA Section</div>
                </div>
              </div>
              
              <button className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-3 rounded-lg font-semibold transition-colors">
                Create Draft
              </button>
              
              <p className="text-xs text-slate-500 text-center">
                Everything is created as a WordPress draft — you stay in full control.
              </p>
            </div>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-indigo-500/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-cyan-500/20 rounded-full blur-3xl" />
        </div>
      </div>
    </section>
  );
};

// Social Proof Section
const SocialProofSection = () => {
  return (
    <section className="py-16 border-y border-white/5" data-testid="social-proof-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-8 text-center">
          <div className="space-y-2">
            <div className="text-3xl font-bold text-white font-['Outfit']">500+</div>
            <div className="text-slate-400">Local Businesses</div>
          </div>
          <div className="space-y-2">
            <div className="text-3xl font-bold text-white font-['Outfit']">50K+</div>
            <div className="text-slate-400">Pages Generated</div>
          </div>
          <div className="space-y-2">
            <div className="text-3xl font-bold text-white font-['Outfit']">50+</div>
            <div className="text-slate-400">Cities per Batch</div>
          </div>
          <div className="space-y-2">
            <div className="text-3xl font-bold text-white font-['Outfit']">2min</div>
            <div className="text-slate-400">Avg. Setup Time</div>
          </div>
        </div>

        {/* Industry identification */}
        <p className="text-center text-slate-500 mt-8 text-sm">
          Trusted by roofers, plumbers, electricians, HVAC, landscapers, and other local service businesses
        </p>
        <p className="text-center text-slate-600 mt-2 text-xs">
          Used across competitive metro areas in roofing, plumbing, HVAC, electrical, and landscaping
        </p>

        {/* Trust badges */}
        <div className="flex flex-wrap justify-center items-center gap-8 mt-8 opacity-50">
          <div className="text-slate-400 font-semibold">Built for local service businesses</div>
          <div className="hidden sm:block w-px h-6 bg-slate-700" />
          <div className="text-slate-400 font-semibold">SEO-first structure</div>
          <div className="hidden sm:block w-px h-6 bg-slate-700" />
          <div className="text-slate-400 font-semibold">Works with your theme</div>
          <div className="hidden sm:block w-px h-6 bg-slate-700" />
          <div className="text-slate-400 font-semibold">Draft-first workflow</div>
        </div>

        {/* Founder note */}
        <div className="mt-12 max-w-2xl mx-auto">
          <div className="card-base text-center">
            <p className="text-slate-300 italic">
              "Hyper Local is new — we're shipping fast and supporting early adopters directly."
            </p>
            <p className="text-slate-500 text-sm mt-2">— The Hyper Local Team</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Results Chart Section - Google Search Console Style
const ResultsChartSection = () => {
  // Generate GSC data with CORRECT axis scaling:
  // Left axis (Clicks): 0-200, so 120 clicks = y=40 (40% from top)
  // Right axis (Impressions): 0-2000, so 1500 impressions = y=25 (25% from top)
  // This ensures 120 clicks appears BELOW 1500 impressions
  // Launch line is at x=8 (middle of August)
  
  const generateRandomData = () => {
    const seed = 88888;
    let random = seed;
    const seededRandom = () => {
      random = (random * 16807) % 2147483647;
      return (random - 1) / 2147483646;
    };
    
    const points = 180;
    const impressionsData = [];
    const clicksData = [];
    
    const LAUNCH_X = 8; // Middle of August
    const CLICKS_MAX = 200; // Left axis max
    const IMPRESSIONS_MAX = 2000; // Right axis max
    
    // Convert real values to SVG Y coordinates (y=0 is top, y=100 is bottom)
    const clicksToY = (clicks) => 100 - (clicks / CLICKS_MAX * 100);
    const impressionsToY = (impressions) => 100 - (impressions / IMPRESSIONS_MAX * 100);
    
    for (let i = 0; i < points; i++) {
      const x = (i / (points - 1)) * 100;
      
      if (x <= LAUNCH_X) {
        // BEFORE LAUNCH: Flat and low
        // ~200 impressions/day, ~1 click/day
        const impressions = 200 + seededRandom() * 50;
        const clicks = 1 + seededRandom() * 2;
        
        impressionsData.push({ x, y: impressionsToY(impressions) });
        clicksData.push({ x, y: clicksToY(clicks) });
      } else {
        // AFTER LAUNCH: Growth begins
        const timeSinceLaunch = (x - LAUNCH_X) / (100 - LAUNCH_X); // 0 to 1
        
        // Impressions grow from ~200 to ~1500
        const baseImpressions = 200 + timeSinceLaunch * 1300;
        const impWobble = (seededRandom() - 0.5) * 150;
        const impressions = Math.max(150, Math.min(1800, baseImpressions + impWobble));
        
        // Clicks grow from ~1 to ~120 with spiky random behavior
        const baseClicks = 1 + timeSinceLaunch * 119;
        const r1 = seededRandom();
        const r2 = seededRandom();
        
        let clicks;
        if (r1 > 0.80) {
          // Spike up (20% chance)
          clicks = baseClicks + r2 * 40;
        } else if (r1 > 0.55) {
          // Medium activity (25% chance)
          clicks = baseClicks + r2 * 20;
        } else {
          // Back toward baseline (55% chance)
          clicks = baseClicks * 0.3 + r2 * 15;
        }
        clicks = Math.max(1, Math.min(180, clicks));
        
        impressionsData.push({ x, y: impressionsToY(impressions) });
        clicksData.push({ x, y: clicksToY(clicks) });
      }
    }
    
    const toPath = (data) => {
      return data.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
    };
    
    return { impressionsPath: toPath(impressionsData), clicksPath: toPath(clicksData) };
  };
  
  const { impressionsPath, clicksPath } = generateRandomData();

  return (
    <section className="py-24" data-testid="results-chart-section">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="overline">Real Results</div>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mt-4">
            Watch your search visibility grow
          </h2>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto">
            Hyper Local customers see measurable improvements in Google Search Console within weeks of launching their service pages.
          </p>
        </div>

        {/* Chart Container - GSC Style */}
        <div className="bg-white rounded-xl p-6 md:p-8 shadow-2xl">
          {/* Chart Header */}
          <div className="flex items-center justify-between mb-2 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#4285f4]"></div>
                <span className="text-gray-600 text-sm font-medium">Total clicks</span>
                <span className="text-gray-900 text-sm font-semibold">16.2K</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#a855f7]"></div>
                <span className="text-gray-600 text-sm font-medium">Total impressions</span>
                <span className="text-gray-900 text-sm font-semibold">211K</span>
              </div>
            </div>
            <div className="text-gray-400 text-xs">Last 6 months</div>
          </div>

          {/* Chart */}
          <div className="relative mt-4">
            {/* Y-axis labels - Clicks (left) - Scale 0-200 so 120 is at 40% from top */}
            <div className="absolute left-0 top-0 bottom-8 w-10 flex flex-col justify-between text-xs text-gray-400 font-medium">
              <span>200</span>
              <span>150</span>
              <span>100</span>
              <span>50</span>
              <span>0</span>
            </div>
            
            {/* Y-axis labels - Impressions (right) - Scale 0-2K so 1500 is at 25% from top */}
            <div className="absolute right-0 top-0 bottom-8 w-10 flex flex-col justify-between text-xs text-gray-400 text-right font-medium">
              <span>2K</span>
              <span>1.5K</span>
              <span>1K</span>
              <span>500</span>
              <span>0</span>
            </div>

            {/* Chart Area */}
            <div className="ml-12 mr-12 relative">
              {/* Implementation marker - positioned at 8% (middle of August) */}
              <div className="absolute z-10 flex flex-col items-center" style={{left: '8%', top: 0, bottom: '32px'}}>
                <div className="bg-green-500 text-white text-xs px-2 py-1 rounded font-medium whitespace-nowrap shadow-lg">
                  Hyper Local launched
                </div>
                <div className="w-px flex-1 border-l-2 border-dashed border-green-400"></div>
              </div>

              <svg viewBox="0 0 100 100" className="w-full h-64" preserveAspectRatio="none">
                {/* Grid lines */}
                <line x1="0" y1="20" x2="100" y2="20" stroke="#f1f5f9" strokeWidth="0.3" />
                <line x1="0" y1="40" x2="100" y2="40" stroke="#f1f5f9" strokeWidth="0.3" />
                <line x1="0" y1="60" x2="100" y2="60" stroke="#f1f5f9" strokeWidth="0.3" />
                <line x1="0" y1="80" x2="100" y2="80" stroke="#f1f5f9" strokeWidth="0.3" />
                
                {/* Gradient definitions */}
                <defs>
                  <linearGradient id="impressionsGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#a855f7" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity="0.02" />
                  </linearGradient>
                  <linearGradient id="clicksGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#4285f4" stopOpacity="0.2" />
                    <stop offset="100%" stopColor="#4285f4" stopOpacity="0.02" />
                  </linearGradient>
                </defs>

                {/* Impressions fill */}
                <path
                  d={impressionsPath + " L 100 100 L 0 100 Z"}
                  fill="url(#impressionsGradient2)"
                />
                
                {/* Clicks fill */}
                <path
                  d={clicksPath + " L 100 100 L 0 100 Z"}
                  fill="url(#clicksGradient2)"
                />
                
                {/* Impressions line (purple) */}
                <path
                  d={impressionsPath}
                  fill="none"
                  stroke="#a855f7"
                  strokeWidth="0.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                
                {/* Clicks line (blue) */}
                <path
                  d={clicksPath}
                  fill="none"
                  stroke="#4285f4"
                  strokeWidth="0.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              {/* X-axis labels */}
              <div className="flex justify-between text-xs text-gray-400 mt-2 font-medium">
                <span>Aug</span>
                <span>Sep</span>
                <span>Oct</span>
                <span>Nov</span>
                <span>Dec</span>
                <span>Jan</span>
              </div>
            </div>
          </div>

          {/* Before/After comparison */}
          <div className="grid grid-cols-2 gap-6 mt-8 pt-6 border-t border-gray-100">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-gray-500 text-xs uppercase tracking-wide mb-2">Before Hyper Local</div>
              <div className="flex items-baseline gap-4">
                <div>
                  <span className="text-2xl font-bold text-gray-400">~1</span>
                  <span className="text-gray-400 text-sm ml-1">click/day</span>
                </div>
                <div>
                  <span className="text-2xl font-bold text-gray-400">~200</span>
                  <span className="text-gray-400 text-sm ml-1">impressions/day</span>
                </div>
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 border border-green-100">
              <div className="text-green-600 text-xs uppercase tracking-wide mb-2">After Hyper Local</div>
              <div className="flex items-baseline gap-4">
                <div>
                  <span className="text-2xl font-bold text-green-600">~120</span>
                  <span className="text-green-600 text-sm ml-1">clicks/day</span>
                </div>
                <div>
                  <span className="text-2xl font-bold text-green-600">~1,500</span>
                  <span className="text-green-600 text-sm ml-1">impressions/day</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-slate-500 text-sm mt-6">
          *Example results from a home services client. Individual results vary based on industry, competition, and existing domain authority.
        </p>
      </div>
    </section>
  );
};

// ============================================
// OPTION 3: Customer Case Study Style - Multiple Testimonials
// ============================================
const CustomerTestimonialsSection = () => {
  const testimonials = [
    {
      initials: "AR",
      company: "Austin Roofing Co.",
      industry: "Residential Roofing • Austin, TX",
      gradient: "from-orange-500 to-red-500",
      quote: "Before Hyper Local: no city rankings. After 90 days: page 1 rankings in 23 cities. We went from invisible to dominating our service area.",
      metrics: {
        impressions: { before: "12", after: "1,847" },
        clicks: { before: "0", after: "127" },
        pages: { before: "5", after: "92" }
      },
      timeframe: "90 days"
    },
    {
      initials: "SP",
      company: "Sunset Plumbing",
      industry: "Emergency Plumbing • Phoenix, AZ",
      gradient: "from-blue-500 to-cyan-500",
      quote: "Before: 1 generic services page. After: 147 city-specific pages ranking across the Phoenix metro. Our phone started ringing from cities we never advertised in.",
      metrics: {
        impressions: { before: "89", after: "3,200" },
        clicks: { before: "3", after: "245" },
        pages: { before: "15", after: "147" }
      },
      timeframe: "60 days"
    },
    {
      initials: "GH",
      company: "Green Horizons Landscaping",
      industry: "Lawn Care & Landscaping • Denver, CO",
      gradient: "from-green-500 to-emerald-500",
      quote: "Before: struggling to rank for anything. After: top result for lawn care in 18 suburbs. Our summer bookings increased 340% year over year.",
      metrics: {
        impressions: { before: "156", after: "4,500" },
        clicks: { before: "8", after: "312" },
        pages: { before: "23", after: "308" }
      },
      timeframe: "120 days"
    }
  ];

  return (
    <section className="py-24" data-testid="customer-testimonials-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="overline">Success Stories</div>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mt-4">
            Real results from real customers
          </h2>
          <p className="text-slate-400 mt-4">See how local businesses transformed their online visibility</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((t, index) => (
            <div key={index} className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${t.gradient} rounded-full flex items-center justify-center text-lg font-bold text-white`}>
                  {t.initials}
                </div>
                <div>
                  <div className="text-white font-semibold">{t.company}</div>
                  <div className="text-slate-400 text-xs">{t.industry}</div>
                </div>
              </div>

              <blockquote className="text-slate-300 italic mb-6 flex-grow leading-relaxed">
                "{t.quote}"
              </blockquote>

              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Impressions</span>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">{t.metrics.impressions.before}</span>
                    <ArrowRight className="w-3 h-3 text-green-400" />
                    <span className="text-white font-semibold">{t.metrics.impressions.after}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Clicks</span>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">{t.metrics.clicks.before}</span>
                    <ArrowRight className="w-3 h-3 text-green-400" />
                    <span className="text-white font-semibold">{t.metrics.clicks.after}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Service Pages</span>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">{t.metrics.pages.before}</span>
                    <ArrowRight className="w-3 h-3 text-green-400" />
                    <span className="text-white font-semibold">{t.metrics.pages.after}</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-700 flex items-center justify-between">
                <span className="text-slate-500 text-xs">Results in {t.timeframe}</span>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xs">★</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-slate-500 text-xs mt-8">
          *Individual results vary based on industry, competition, and existing domain authority.
        </p>
      </div>
    </section>
  );
};

// ============================================
// OPTION 4: Dashboard Mockup
// ============================================
const DashboardMockupSection = () => {
  return (
    <section className="py-24" data-testid="dashboard-mockup-section">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="overline">Real Results</div>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mt-4">
            What your Search Console will look like
          </h2>
          <p className="text-slate-400 mt-4">90 days after launching Hyper Local</p>
        </div>

        {/* GSC Dashboard Mockup */}
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          {/* Browser Chrome */}
          <div className="bg-slate-100 px-4 py-3 flex items-center gap-2 border-b">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
            </div>
            <div className="flex-1 mx-4">
              <div className="bg-white rounded-md px-3 py-1.5 text-sm text-slate-500 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                search.google.com/search-console
              </div>
            </div>
          </div>

          {/* Dashboard Content */}
          <div className="p-6 md:p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <img src="https://www.gstatic.com/images/branding/product/2x/googleg_48dp.png" alt="Google" className="w-8 h-8" />
                <span className="font-medium text-gray-800">Search Console</span>
              </div>
              <div className="text-sm text-gray-500">Last 90 days</div>
            </div>

            {/* Metrics Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="p-4 rounded-lg bg-blue-50">
                <div className="text-blue-600 text-2xl md:text-3xl font-bold">3,847</div>
                <div className="text-gray-600 text-sm">Total clicks</div>
                <div className="text-green-600 text-xs font-medium mt-1">↑ 11,900%</div>
              </div>
              <div className="p-4 rounded-lg bg-purple-50">
                <div className="text-purple-600 text-2xl md:text-3xl font-bold">45.2K</div>
                <div className="text-gray-600 text-sm">Total impressions</div>
                <div className="text-green-600 text-xs font-medium mt-1">↑ 650%</div>
              </div>
              <div className="p-4 rounded-lg bg-green-50">
                <div className="text-green-600 text-2xl md:text-3xl font-bold">8.5%</div>
                <div className="text-gray-600 text-sm">Avg CTR</div>
                <div className="text-green-600 text-xs font-medium mt-1">↑ 340%</div>
              </div>
              <div className="p-4 rounded-lg bg-orange-50">
                <div className="text-orange-600 text-2xl md:text-3xl font-bold">12.3</div>
                <div className="text-gray-600 text-sm">Avg position</div>
                <div className="text-green-600 text-xs font-medium mt-1">↑ from 87</div>
              </div>
            </div>

            {/* Simple Growth Indicator */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white/80 text-sm">Performance vs. previous period</div>
                  <div className="text-3xl font-bold mt-1">+847% overall growth</div>
                </div>
                <div className="text-6xl">📈</div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-slate-500 text-xs mt-8">
          *Simulated dashboard showing typical results. Individual results vary.
        </p>
      </div>
    </section>
  );
};

// Featured Testimonial - Larger single testimonial for emphasis
const FeaturedTestimonialSection = () => {
  return (
    <section className="py-24" data-testid="featured-testimonial-section">
      <div className="max-w-4xl mx-auto px-6">
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-3xl p-8 md:p-12 border border-slate-700 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl"></div>
          
          <div className="relative">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-3xl font-bold text-white">
                MH
              </div>
              <div>
                <div className="text-white font-semibold text-xl">Mike Henderson</div>
                <div className="text-slate-400">Owner, Henderson HVAC Services</div>
                <div className="text-slate-500 text-sm">Dallas-Fort Worth, TX</div>
              </div>
            </div>

            <blockquote className="text-2xl md:text-3xl text-white font-light leading-relaxed mb-8">
              "I was skeptical at first, but within 6 weeks we were getting calls from cities I didn't even know existed in our metro area. Hyper Local paid for itself in the first month."
            </blockquote>

            <div className="grid md:grid-cols-4 gap-4 mb-8">
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-white">67</div>
                <div className="text-slate-400 text-sm">Pages Generated</div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-green-400">+892%</div>
                <div className="text-slate-400 text-sm">More Traffic</div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-white">41</div>
                <div className="text-slate-400 text-sm">Page 1 Rankings</div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-white">3.2x</div>
                <div className="text-slate-400 text-sm">More Leads</div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-6 border-t border-slate-700">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400 text-xl">★</span>
                ))}
              </div>
              <span className="text-slate-400 text-sm">Verified Hyper Local Customer</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Problem-Solution Section
const ProblemSolutionSection = () => {
  return (
    <section className="py-24" data-testid="problem-solution-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Problem */}
          <div className="space-y-6">
            <div className="overline">The Problem</div>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white">
              Google changed the rules.
            </h2>
            <div className="space-y-4 text-slate-400">
              <p>
                <strong className="text-white">Generic "Services" pages don't rank anymore.</strong> Google now favors businesses with dedicated pages for each service in each city they serve.
              </p>
              <p>
                That means if you're a roofer offering 5 services in 30 cities, you need <strong className="text-white">150 pages</strong> — not one.
              </p>
              <p>
                Writing 150+ unique, SEO-optimized pages? That's weeks of work. Or thousands in agency fees.
              </p>
              <p className="text-amber-400/90 font-medium">
                Meanwhile, your competitors are quietly building these pages and taking the clicks that should be yours.
              </p>
            </div>
          </div>

          {/* Solution */}
          <div className="space-y-6">
            <div className="overline text-indigo-400">The Solution</div>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white">
              Hyper Local builds your city pages in minutes.
            </h2>
            <div className="space-y-4 text-slate-400">
              <p>
                We generate SEO-structured <strong className="text-white">service + city pages</strong> that follow Google's guidelines — directly inside your WordPress site.
              </p>
              <p>
                <strong className="text-cyan-400">No thin content. No doorway page penalties.</strong> Just clean, rankable pages ready for your review.
              </p>
              <p>
                Pages are created as WordPress drafts so you maintain full control — nothing goes live without your approval.
              </p>
              <a href="/why-hyper-local" className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 font-medium mt-2">
                Learn why this matters now →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const benefits = [
    {
      icon: <Layers className="w-8 h-8" />,
      title: "Consistent layout across every page",
      description: "Every service-area page follows your brand's structure — so 50 city pages look as polished as your homepage."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "SEO essentials baked in",
      description: "Proper H1s, meta descriptions, LocalBusiness & Service schema, and internal linking — no SEO guesswork required."
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Draft-first control",
      description: "Everything is created as a WordPress draft — you stay in full control. Nothing goes live without your approval."
    },
    {
      icon: <Check className="w-8 h-8" />,
      title: "No doorway-page junk",
      description: "Pages are structured for quality and relevance — not thin or risky SEO tactics that could hurt your site."
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Works with Yoast / RankMath",
      description: "Full compatibility with popular WordPress SEO plugins — no reconfiguration or technical headaches."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Bulk generation without headaches",
      description: "Generate hundreds of pages in minutes instead of weeks of writing or thousands in agency work."
    }
  ];

  return (
    <section id="benefits" className="py-24 grid-bg" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="overline">Why Hyper Local</div>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mt-4">
            Everything you need to dominate local search
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div 
              key={index} 
              className="card-base group"
              data-testid={`benefit-card-${index}`}
            >
              <div className="w-14 h-14 bg-indigo-500/10 rounded-xl flex items-center justify-center text-indigo-400 mb-4 group-hover:bg-indigo-500/20 transition-colors">
                {benefit.icon}
              </div>
              <h3 className="font-['Outfit'] text-xl font-semibold text-white mb-2">
                {benefit.title}
              </h3>
              <p className="text-slate-400">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Secret Weapon Section - Internal Linking
const SecretWeaponSection = () => {
  return (
    <section className="py-24 relative overflow-hidden" data-testid="secret-weapon-section">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/20 via-transparent to-transparent pointer-events-none" />
      
      <div className="max-w-5xl mx-auto px-6 relative">
        <div className="text-center mb-12">
          <div className="inline-block bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1 mb-6">
            <span className="text-cyan-400 text-sm font-semibold">Competitive Advantage</span>
          </div>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            The SEO advantage your competitors don't have.
          </h2>
        </div>

        <div className="card-base p-8 md:p-12 border-cyan-500/20">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Left - Explanation */}
            <div className="space-y-6">
              <p className="text-lg text-slate-300">
                Most page generators just create pages. <strong className="text-white">Hyper Local automatically builds internal links between your service pages</strong> — the same structure enterprise SEO teams spend thousands building manually.
              </p>
              
              <div className="bg-slate-900/50 rounded-lg p-6 border border-white/5">
                <p className="text-slate-400 text-sm mb-3 uppercase tracking-wide font-semibold">Why it matters</p>
                <p className="text-slate-300">
                  Google uses internal links to understand your site structure and pass ranking power between pages. Without them, your city pages are islands. <strong className="text-cyan-400">With them, they're a network.</strong>
                </p>
              </div>
              
              <p className="text-slate-400 text-sm italic">
                This is the same structure enterprise SEO teams build manually — Hyper Local does it automatically.
              </p>
            </div>

            {/* Right - Visual */}
            <div className="relative">
              <div className="bg-slate-900/80 rounded-xl p-6 border border-white/10">
                <p className="text-slate-500 text-xs uppercase tracking-wide mb-6">Hub & Spoke Link Structure</p>
                
                {/* Hierarchical visual representation */}
                <div className="space-y-4">
                  {/* Service Hub - Top Level */}
                  <div className="flex justify-center">
                    <div className="bg-indigo-600/30 border border-indigo-500/50 rounded-lg px-4 py-2 text-center">
                      <p className="text-indigo-300 font-semibold text-sm">Residential Electrical</p>
                      <p className="text-indigo-400/60 text-xs">Service Hub</p>
                    </div>
                  </div>
                  
                  {/* Bidirectional arrows */}
                  <div className="flex justify-center items-center gap-1">
                    <div className="text-indigo-400 text-xs">↓</div>
                    <div className="text-cyan-400 text-xs">↑</div>
                  </div>
                  
                  {/* City Hubs - Middle Level */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-cyan-600/20 border border-cyan-500/30 rounded-lg px-3 py-2 text-center">
                      <p className="text-cyan-300 font-medium text-sm">Dallas, TX</p>
                      <p className="text-cyan-400/60 text-xs">City Hub</p>
                    </div>
                    <div className="bg-cyan-600/20 border border-cyan-500/30 rounded-lg px-3 py-2 text-center">
                      <p className="text-cyan-300 font-medium text-sm">Fort Worth, TX</p>
                      <p className="text-cyan-400/60 text-xs">City Hub</p>
                    </div>
                  </div>
                  
                  {/* Bidirectional arrows */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex justify-center items-center gap-1">
                      <div className="text-cyan-400 text-xs">↓</div>
                      <div className="text-slate-400 text-xs">↑</div>
                    </div>
                    <div className="flex justify-center items-center gap-1">
                      <div className="text-cyan-400 text-xs">↓</div>
                      <div className="text-slate-400 text-xs">↑</div>
                    </div>
                  </div>
                  
                  {/* Service Pages - Bottom Level */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <div className="bg-slate-800/80 rounded px-2 py-1.5 text-center">
                        <p className="text-slate-300 text-xs">Panel Replacement</p>
                      </div>
                      <div className="bg-slate-800/80 rounded px-2 py-1.5 text-center">
                        <p className="text-slate-300 text-xs">Ceiling Fan Install</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="bg-slate-800/80 rounded px-2 py-1.5 text-center">
                        <p className="text-slate-300 text-xs">Panel Replacement</p>
                      </div>
                      <div className="bg-slate-800/80 rounded px-2 py-1.5 text-center">
                        <p className="text-slate-300 text-xs">Ceiling Fan Install</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-slate-500 text-xs text-center">Service Pages</p>
                </div>

                <div className="mt-6 pt-4 border-t border-white/5">
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <Check className="w-4 h-4" />
                    <span>Links created automatically</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-400 text-sm mt-2">
                    <Check className="w-4 h-4" />
                    <span>Follows Google best practices</span>
                  </div>
                </div>
              </div>
              
              {/* Decorative glow */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-cyan-500/10 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Connect WordPress",
      description: "Install our plugin and enter your API key. Takes less than 2 minutes."
    },
    {
      number: "02",
      title: "Choose Service + City",
      description: "Select your services and target cities directly from our interface."
    },
    {
      number: "03",
      title: "Create drafts & publish",
      description: "Review generated pages in your WordPress dashboard and publish when ready."
    }
  ];

  return (
    <section id="how-it-works" className="py-24" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="overline">Simple Process</div>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mt-4">
            How it works
          </h2>
        </div>

        <div className="relative">
          {/* Timeline line */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-indigo-500 via-cyan-500 to-indigo-500" />

          <div className="space-y-12 lg:space-y-0">
            {steps.map((step, index) => (
              <div 
                key={index}
                className={`lg:flex items-center gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                data-testid={`step-${index}`}
              >
                <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:text-right lg:pr-16' : 'lg:text-left lg:pl-16'}`}>
                  <div className="card-base inline-block">
                    <span className="text-6xl font-['Outfit'] font-bold text-indigo-500/30">{step.number}</span>
                    <h3 className="font-['Outfit'] text-2xl font-semibold text-white mt-2">{step.title}</h3>
                    <p className="text-slate-400 mt-2 max-w-md">{step.description}</p>
                  </div>
                </div>
                
                {/* Center dot */}
                <div className="hidden lg:flex w-4 h-4 bg-indigo-500 rounded-full border-4 border-slate-900 z-10" />
                
                <div className="lg:w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
// Page Calculator Component
const PageCalculator = () => {
  const [cities, setCities] = useState('10');
  const [services, setServices] = useState('5');
  
  // Parse values for calculation (default to 0 if empty)
  const citiesNum = parseInt(cities) || 0;
  const servicesNum = parseInt(services) || 0;
  
  // Formula: approx pages = (services * cities) + cities
  const approxPages = (servicesNum * citiesNum) + citiesNum;
  
  // Determine recommended plan based on page count
  const getRecommendedPlan = () => {
    if (approxPages <= 5) return { name: 'Free', color: 'text-slate-400' };
    if (approxPages <= 50) return { name: 'Starter', color: 'text-cyan-400' };
    if (approxPages <= 200) return { name: 'Growth', color: 'text-green-400' };
    if (approxPages <= 500) return { name: 'Pro', color: 'text-indigo-400' };
    return { name: 'Agency', color: 'text-purple-400' };
  };
  
  const recommended = getRecommendedPlan();

  return (
    <section className="py-16" data-testid="page-calculator-section">
      <div className="max-w-2xl mx-auto px-6">
        <div className="card-base p-8">
          <div className="text-center mb-8">
            <h3 className="font-['Outfit'] text-2xl font-bold text-white">
              How many pages do you need?
            </h3>
            <p className="text-slate-400 mt-2 text-sm">
              Estimate your page count based on your service area
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Cities Input */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Cities you serve
              </label>
              <input
                type="number"
                min="0"
                max="500"
                value={cities}
                onChange={(e) => setCities(e.target.value)}
                onBlur={(e) => {
                  if (e.target.value === '' || parseInt(e.target.value) < 1) {
                    setCities('1');
                  }
                }}
                className="input-dark w-full text-center text-xl font-semibold"
                placeholder="10"
              />
            </div>

            {/* Services Input */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Services you offer
              </label>
              <input
                type="number"
                min="0"
                max="50"
                value={services}
                onChange={(e) => setServices(e.target.value)}
                onBlur={(e) => {
                  if (e.target.value === '' || parseInt(e.target.value) < 1) {
                    setServices('1');
                  }
                }}
                className="input-dark w-full text-center text-xl font-semibold"
                placeholder="5"
              />
            </div>
          </div>

          {/* Result */}
          <div className="bg-slate-900/50 rounded-xl p-6 text-center">
            <div className="text-slate-400 text-sm mb-2">Approximate pages needed</div>
            <div className="text-5xl font-['Outfit'] font-bold text-white mb-2">
              ~{approxPages.toLocaleString()}
            </div>
            <div className="text-slate-500 text-xs mb-4">
              ({servicesNum} services × {citiesNum} cities) + {citiesNum} city pages
            </div>
            <div className="flex items-center justify-center gap-2">
              <span className="text-slate-400 text-sm">Recommended plan:</span>
              <span className={`font-semibold ${recommended.color}`}>{recommended.name}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PricingSection = () => {
  const navigate = useNavigate();
  
  const plans = [
    {
      id: "free",
      name: "Free",
      price: 0,
      originalPrice: null,
      description: "Try it out, no commitment",
      features: [
        "5 pages",
        "Basic generation",
        "Email support"
      ]
    },
    {
      id: "starter",
      name: "Starter",
      price: 29,
      originalPrice: 49,
      description: "Perfect for new businesses just getting off the ground",
      features: [
        "50 pages",
        "Bulk generation",
        "Internal linking",
        "Email support"
      ]
    },
    {
      id: "growth",
      name: "Growth",
      price: 49,
      originalPrice: 99,
      description: "Perfect for single-location businesses",
      features: [
        "200 pages",
        "Bulk generation",
        "Internal linking",
        "Priority support"
      ]
    },
    {
      id: "pro",
      name: "Pro",
      price: 99,
      originalPrice: 199,
      description: "For growing multi-location businesses",
      popular: true,
      features: [
        "500 pages",
        "Bulk generation",
        "Internal linking",
        "Priority support"
      ]
    },
    {
      id: "agency",
      name: "Agency",
      price: 299,
      originalPrice: 499,
      description: "For agencies managing multiple clients",
      features: [
        "2,000 pages",
        "Bulk generation",
        "Internal linking",
        "24/7 support"
      ]
    }
  ];

  const handleSelectPlan = async (planId) => {
    // Free plan - just go to signup
    if (planId === 'free') {
      navigate('/signup');
      return;
    }
    
    // Paid plans - go to Stripe checkout
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        // Not logged in - redirect to signup first, then they can upgrade
        toast.info('Create an account first, then subscribe to a plan.');
        navigate('/signup');
        return;
      }
      
      const response = await axios.post(
        `${API_URL}/api/checkout/session`, 
        {
          plan_name: planId,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      if (error.response?.status === 401) {
        toast.info('Please log in to subscribe to a plan.');
        navigate('/login');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <section id="pricing" className="py-24 grid-bg" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          {/* Founders Pricing Badge */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-full px-4 py-2 mb-6">
            <span className="text-xl">🚀</span>
            <span className="text-amber-400 font-semibold">Founders Pricing</span>
            <span className="text-amber-400/70">•</span>
            <span className="text-amber-300 text-sm">First 500 customers only</span>
          </div>
          
          <div className="overline">Pricing</div>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mt-4">
            Lock in Founders pricing forever
          </h2>
          <p className="text-slate-400 mt-4">Join now and keep these rates for life. Cancel anytime.</p>
          <p className="text-slate-500 mt-2 text-sm">No long-term contracts. No setup fees. Keep every page you generate.</p>
        </div>

        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
          {plans.map((plan) => (
            <div 
              key={plan.id}
              className={`relative rounded-2xl p-6 ${
                plan.popular 
                  ? 'bg-indigo-950/30 border-2 border-indigo-500/50 mt-4' 
                  : 'card-base mt-4'
              }`}
              data-testid={`pricing-card-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                  <span className="bg-indigo-600 text-white text-sm font-semibold px-4 py-1 rounded-full whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="font-['Outfit'] text-xl font-semibold text-white">{plan.name}</h3>
                <div className="mt-4">
                  {plan.price === 0 ? (
                    <span className="text-4xl font-['Outfit'] font-bold text-white">Free</span>
                  ) : (
                    <>
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="text-slate-500 line-through text-lg">${plan.originalPrice}</span>
                        <span className="bg-green-500/20 text-green-400 text-xs font-semibold px-2 py-0.5 rounded-full">
                          Save {Math.round((1 - plan.price / plan.originalPrice) * 100)}%
                        </span>
                      </div>
                      <span className="text-4xl font-['Outfit'] font-bold text-white">${plan.price}</span>
                      <span className="text-slate-400">/mo</span>
                    </>
                  )}
                </div>
                <p className="text-slate-400 mt-2 text-sm">{plan.description}</p>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(plan.id)}
                className={`w-full py-3 rounded-full font-semibold transition-all relative z-10 ${
                  plan.popular 
                    ? 'btn-primary' 
                    : plan.id === 'free'
                    ? 'bg-slate-700 hover:bg-slate-600 text-white'
                    : 'btn-secondary'
                }`}
                data-testid={`select-plan-${plan.id}`}
              >
                {plan.id === 'free' ? 'Start Free' : 'Subscribe'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "Will these pages show in WP admin?",
      answer: "Yes! All generated pages appear as drafts in your WordPress dashboard. You have full control to edit, review, and publish them whenever you're ready."
    },
    {
      question: "Does it work with my theme?",
      answer: "Absolutely. Hyper Local generates content that uses your theme's default styling. The pages look native to your site design."
    },
    {
      question: "Is this doorway pages?",
      answer: "No. Unlike doorway pages that offer thin, duplicate content, Hyper Local creates unique, locally-relevant content for each page. Each page includes specific local information and follows Google's quality guidelines."
    },
    {
      question: "Can I edit the content?",
      answer: "Yes! All pages are created as drafts. You can edit any aspect — headlines, content, images, CTAs — before publishing. You maintain full editorial control."
    },
    {
      question: "Does it work with Yoast/RankMath?",
      answer: "Yes. Hyper Local is fully compatible with Yoast SEO and RankMath. Generated pages include proper meta descriptions, titles, and schema markup that these plugins can recognize and optimize."
    },
    {
      question: "Can I bulk generate pages?",
      answer: "Yes! Use our interface to select your services and target cities, then generate hundreds of pages in minutes instead of weeks. No manual CSV uploads needed — everything is done within the app."
    },
    {
      question: "What about citations/images/maps?",
      answer: "Local business citations and Google Maps embeds are coming soon as optional features. Currently, you can add these manually to generated pages before publishing."
    },
    {
      question: "What if I cancel?",
      answer: "All pages managed and maintained for freshness by Hyper Local will be removed from the site."
    }
  ];

  return (
    <section id="faq" className="py-24" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="overline">FAQ</div>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mt-4">
            Frequently asked questions
          </h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className="card-base cursor-pointer"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              data-testid={`faq-item-${index}`}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-['Outfit'] font-semibold text-white pr-4">{faq.question}</h3>
                <ChevronDown 
                  className={`w-5 h-5 text-slate-400 transition-transform flex-shrink-0 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </div>
              {openIndex === index && (
                <p className="text-slate-400 mt-4 leading-relaxed">{faq.answer}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Lead Capture Form Section
const LeadCaptureSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website_url: '',
    business_type: '',
    uses_wordpress: true
  });
  const [loading, setLoading] = useState(false);

  const businessTypes = [
    "Roofing",
    "HVAC",
    "Plumbing",
    "Electrical",
    "Landscaping",
    "Cleaning Services",
    "Home Improvement",
    "Legal Services",
    "Medical/Dental",
    "Real Estate",
    "Other"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/lead`, formData);
      toast.success('Thank you! We\'ll be in touch soon.');
      navigate('/thanks');
    } catch (error) {
      console.error('Lead submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lead-form" className="py-24 grid-bg" data-testid="lead-capture-section">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="overline">Get Started</div>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-white mt-4">
            Join the waitlist
          </h2>
          <p className="text-slate-400 mt-4">
            Be the first to know when we launch new features.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card-base space-y-6" data-testid="lead-form">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Your Name</label>
            <input
              type="text"
              required
              className="input-dark w-full"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              data-testid="lead-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
            <input
              type="email"
              required
              className="input-dark w-full"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              data-testid="lead-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Website URL</label>
            <input
              type="url"
              required
              placeholder="https://yoursite.com"
              className="input-dark w-full"
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              data-testid="lead-website-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Business Type</label>
            <select
              required
              className="input-dark w-full"
              value={formData.business_type}
              onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
              data-testid="lead-business-type-select"
            >
              <option value="">Select your industry</option>
              {businessTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="uses_wordpress"
              checked={formData.uses_wordpress}
              onChange={(e) => setFormData({ ...formData, uses_wordpress: e.target.checked })}
              className="w-5 h-5 rounded border-slate-700 bg-slate-900 text-indigo-600 focus:ring-indigo-500"
              data-testid="lead-wordpress-checkbox"
            />
            <label htmlFor="uses_wordpress" className="text-slate-300">I use WordPress</label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="lead-submit-btn"
          >
            {loading ? 'Submitting...' : 'Create My First Service-Area Page'}
          </button>
          
          <p className="text-center text-slate-500 text-sm">
            No credit card required • Draft-first • Cancel anytime
          </p>
        </form>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  return (
    <section className="py-24" data-testid="final-cta-section">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div className="relative bg-gradient-to-r from-indigo-900/50 via-indigo-800/30 to-indigo-900/50 rounded-3xl p-12 md:p-16 overflow-hidden">
          {/* Background glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 via-cyan-500/10 to-indigo-500/10" />
          
          <div className="relative z-10">
            <h2 className="font-['Outfit'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Stop losing clicks to competitors with more city pages.
            </h2>
            
            <p className="text-slate-400 mb-8 text-lg">
              Create your first service-area page in minutes. Draft-first, so you stay in control.
            </p>

            <a 
              href="#pricing" 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="final-cta-btn"
            >
              Create My First Service-Area Page
              <ArrowRight className="w-5 h-5" />
            </a>
            
            <p className="text-slate-500 text-sm mt-6">No credit card required • Draft-first • Cancel anytime</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-['Outfit'] font-bold text-white">Hyper Local</span>
          </div>

          <nav className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="#benefits" className="text-slate-400 hover:text-white transition-colors">Benefits</a>
            <a href="#how-it-works" className="text-slate-400 hover:text-white transition-colors">How it Works</a>
            <a href="#pricing" className="text-slate-400 hover:text-white transition-colors">Pricing</a>
            <a href="#faq" className="text-slate-400 hover:text-white transition-colors">FAQ</a>
          </nav>

          <div className="text-slate-500 text-sm">
            Contact: <a href="mailto:hello@hyperlocal.io" className="text-slate-400 hover:text-white">hello@hyperlocal.io</a>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-white/5 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} Hyper Local. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#020617]">
      <StickyHeader />
      <HeroSection />
      <SocialProofSection />
      <DashboardMockupSection />
      <ProblemSolutionSection />
      <BenefitsSection />
      <CustomerTestimonialsSection />
      <SecretWeaponSection />
      <HowItWorksSection />
      <FeaturedTestimonialSection />
      <PageCalculator />
      <PricingSection />
      <FAQSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
};

export default LandingPage;
